import { useEffect, useState } from "react";
import { RotateCcw, RefreshCcw, Undo2, Package } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, fmtDate, formatApiErrorDetail } from "@/lib/api";

export default function Ndr() {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () =>
    appApi.get("orders").then((d) => setOrders((d.orders || []).filter((o) => String(o.status).toLowerCase() === "ndr"))).catch(() => setOrders([]));
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (o, action) => {
    const label = action === "reattempt" ? "Re-attempt delivery" : "Return to origin (RTO)";
    if (!window.confirm(`${label} for ${o.awb}?`)) return;
    setBusy(o.id + action);
    try {
      await appApi.post(`orders/${o.id}/ndr-action`, { action });
      toast.success(action === "reattempt" ? `Re-attempt scheduled for ${o.awb}.` : `${o.awb} marked for RTO.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="ndr-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">NDR</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Failed deliveries</h1>
        <p className="text-sm text-zinc-500 mt-2">Re-attempt or return to origin with one tap — synced with the app instantly.</p>
      </div>

      <div className="space-y-4" data-testid="ndr-list">
        {(orders || []).map((o) => (
          <div key={o.id} className="border-2 border-obsidian bg-white p-6" data-testid={`ndr-card-${o.awb}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-red-600 text-white flex items-center justify-center"><Package className="w-5 h-5" /></span>
                <div>
                  <p className="font-mono text-sm font-bold">{o.awb}</p>
                  <p className="text-xs text-zinc-500">{o.courier_display_name || o.courier_name} · {fmtDate(o.created_at)}</p>
                </div>
              </div>
              <span className="font-mono text-xs font-bold bg-red-600 text-white px-2 py-0.5 uppercase tracking-wider">NDR</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
              <div>
                <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Customer</p>
                <p className="font-semibold mt-1">{o.delivery?.name} · {o.delivery?.phone}</p>
                <p className="text-xs text-zinc-500">{o.delivery?.city}, {o.delivery?.state} — {o.delivery?.pincode}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Reason</p>
                <p className="text-xs text-zinc-600 mt-1">{o.pending_message || o.remarks || "Consignee unavailable / address issue"}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Value</p>
                <p className="font-mono text-xs font-bold mt-1">{o.rate != null ? inr(o.rate) : "—"} · {(o.payment_mode || "").toUpperCase()}</p>
              </div>
            </div>
            <div className="flex gap-3 mt-5 flex-wrap">
              <button onClick={() => act(o, "reattempt")} disabled={busy} data-testid={`ndr-reattempt-${o.awb}`}
                className="inline-flex items-center gap-2 bg-acid text-white text-xs font-bold uppercase tracking-widest px-5 py-3 border-2 border-obsidian shadow-[3px_3px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-60">
                <RefreshCcw className="w-4 h-4" /> {busy === o.id + "reattempt" ? "Working…" : "Re-attempt"}
              </button>
              <button onClick={() => act(o, "rto")} disabled={busy} data-testid={`ndr-rto-${o.awb}`}
                className="inline-flex items-center gap-2 bg-white text-red-600 text-xs font-bold uppercase tracking-widest px-5 py-3 border-2 border-red-600 hover:bg-red-50 transition-colors duration-150 disabled:opacity-60">
                <Undo2 className="w-4 h-4" /> {busy === o.id + "rto" ? "Working…" : "Return to Origin"}
              </button>
            </div>
          </div>
        ))}
        {orders && orders.length === 0 && (
          <div className="border-2 border-obsidian bg-white p-10 flex items-center gap-4" data-testid="ndr-empty">
            <span className="w-10 h-10 bg-acid text-white flex items-center justify-center"><RotateCcw className="w-5 h-5" /></span>
            <p className="text-sm text-zinc-600">Zero failed deliveries right now. The beast is fed.</p>
          </div>
        )}
        {!orders && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Scanning for NDR…</p>}
      </div>
    </div>
  );
}
